import { useEffect, useRef } from 'react'
import { useYouTubePlayer } from '../hooks/useYouTubePlayer.js'
import { completeCaptureJob, removeJob, type MixSettings } from '../lib/api.js'
import { storageUpload } from '../lib/storageHeaders.js'

export interface CaptureEngineProps {
  videoId:      string
  stream:       MediaStream
  jobId:        string
  uploadUrl:    string
  mixSettings?: MixSettings
  onProgress:   (elapsed: number, expected: number) => void
  onComplete:   (jobId: string) => void
  onError:      (err: Error)    => void
}

/**
 * Headless component — renders only a hidden wrapper for the YouTube iframe.
 * All recording logic lives here. Zero visible UI.
 *
 * StrictMode-safe: destructive cleanup (removeJob, recorder.stop) is deferred
 * via setTimeout(0) so it gets cancelled on the synchronous re-mount.
 */
export default function CaptureEngine({
  videoId, stream, jobId, uploadUrl,
  onProgress, onComplete, onError,
}: CaptureEngineProps) {
  const wrapperRef     = useRef<HTMLDivElement>(null)
  const chunksRef      = useRef<Blob[]>([])
  const recorderRef    = useRef<MediaRecorder | null>(null)
  const timerRef       = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedRef     = useRef(false)
  const completedRef   = useRef(false)
  const capturingRef   = useRef(false)
  const warmRef        = useRef(false)
  const rateEnforcerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Stable refs for callbacks to avoid stale closures
  const onProgressRef = useRef(onProgress)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef    = useRef(onError)
  useEffect(() => { onProgressRef.current = onProgress }, [onProgress])
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])
  useEffect(() => { onErrorRef.current = onError }, [onError])

  const jobIdRef     = useRef(jobId)
  const uploadUrlRef = useRef(uploadUrl)
  useEffect(() => { jobIdRef.current = jobId }, [jobId])
  useEffect(() => { uploadUrlRef.current = uploadUrl }, [uploadUrl])

  // ── Player target div ──────────────────────────────────────────────────────
  // YouTube IFrame API replaces the inner div with an iframe. We create it
  // imperatively so React doesn't track it (avoids removeChild errors) and
  // so we can recreate it after StrictMode destroys the player.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return
    wrapper.innerHTML = ''
    const div = document.createElement('div')
    div.id = 'capture-engine-player'
    wrapper.appendChild(div)
    return () => { wrapper.innerHTML = '' }
  }, [])

  const { playerRef, ytReady } = useYouTubePlayer({
    containerId:   'capture-engine-player',
    videoId,
    onStateChange: (state: number) => {
      // PLAYING = 1 — set 2x speed once playback actually starts
      if (state === 1 && capturingRef.current) {
        playerRef.current?.setPlaybackRate(2)
      }
      // ENDED = 0
      if (state === 0 && capturingRef.current) {
        stopCapture()
      }
    },
  })

  const stopCapture = () => {
    capturingRef.current = false
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    if (timerRef.current) clearInterval(timerRef.current)
    if (rateEnforcerRef.current) clearInterval(rateEnforcerRef.current)
    playerRef.current?.stopVideo()
  }

  const beginRecording = () => {
    if (startedRef.current) return
    startedRef.current = true

    const audioTracks = stream.getAudioTracks()
    if (audioTracks.length === 0 || audioTracks.some(t => t.readyState === 'ended')) {
      onErrorRef.current(new Error('No audio tracks available'))
      return
    }

    const MIME_CANDIDATES = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      '',
    ]
    const mimeType = MIME_CANDIDATES.find(
      m => m === '' || MediaRecorder.isTypeSupported(m),
    ) ?? ''

    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {})
    } catch {
      try { recorder = new MediaRecorder(stream) }
      catch {
        onErrorRef.current(new Error('Could not start recording'))
        return
      }
    }

    chunksRef.current = []
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
    recorder.onstop = () => { void doUpload() }

    try {
      recorder.start(1000)
    } catch {
      onErrorRef.current(new Error('Could not start recording'))
      return
    }
    recorderRef.current = recorder

    playerRef.current?.unMute()
    playerRef.current?.setPlaybackRate(2) // Set on cued video before play
    playerRef.current?.playVideo()

    capturingRef.current = true

    // Enforce 2x rate — YouTube treats setPlaybackRate as a suggestion and may
    // reset it (after ads, buffering, etc.). Poll every 5s and correct.
    rateEnforcerRef.current = setInterval(() => {
      if (!capturingRef.current) return
      const rate = playerRef.current?.getPlaybackRate()
      if (rate != null && rate !== 2) playerRef.current?.setPlaybackRate(2)
    }, 5000)

    timerRef.current = setInterval(() => {
      const dur = playerRef.current?.getDuration?.() || 0
      const playerTime = playerRef.current?.getCurrentTime?.() || 0
      // Show video position vs full duration — progress advances at 2x wall time
      onProgressRef.current(Math.floor(playerTime), dur > 0 ? Math.ceil(dur) : 0)

      // Fallback: if ENDED event didn't fire, stop capture once the player
      // has reached the end of the video.
      if (dur > 0 && playerTime >= dur - 1 && capturingRef.current) {
        stopCapture()
      }
    }, 1000)
  }

  const doUpload = async () => {
    const mimeType = recorderRef.current?.mimeType || 'audio/webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })
    try {
      const res = await storageUpload(uploadUrlRef.current, blob, mimeType)
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
      await completeCaptureJob(jobIdRef.current)
      completedRef.current = true
      onCompleteRef.current(jobIdRef.current)
    } catch (err) {
      onErrorRef.current(err as Error)
    }
  }

  // Player ready — start recording (or resume playback after StrictMode re-mount)
  useEffect(() => {
    if (!ytReady) return
    if (startedRef.current) {
      // StrictMode re-mount: player was recreated, resume playback
      playerRef.current?.unMute()
      playerRef.current?.playVideo()
      return
    }
    beginRecording()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytReady])

  // ── Cleanup on unmount (StrictMode-safe) ───────────────────────────────────
  // setTimeout(0) defers destructive actions past the synchronous StrictMode
  // unmount-remount cycle. On real unmount the timeout fires normally.
  useEffect(() => {
    const t = setTimeout(() => { warmRef.current = true }, 0)
    return () => {
      clearTimeout(t)
      if (!warmRef.current) return // StrictMode double-mount — skip
      if (timerRef.current) clearInterval(timerRef.current)
      if (rateEnforcerRef.current) clearInterval(rateEnforcerRef.current)
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
      if (jobIdRef.current && !completedRef.current) {
        void removeJob(jobIdRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // YouTube IFrame API requires a minimum 200x200px viewport for full
  // functionality (setPlaybackRate, etc.). Position offscreen at full size —
  // do NOT use overflow:hidden on a parent, as getBoundingClientRect() would
  // report clipped dimensions and YouTube still enters reduced mode.
  return (
    <div
      ref={wrapperRef}
      style={{
        position:      'fixed',
        width:         '480px',
        height:        '270px',
        top:           '-9999px',
        left:          '-9999px',
        pointerEvents: 'none',
      }}
    />
  )
}
